import { Component, DestroyRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { SearchResultDevice } from '@app/core/interfaces/search-result-device';
import { SearchResultDomain } from '@app/core/interfaces/search-result-domain';
import { SearchResultEnterprise } from '@app/core/interfaces/search-result-enterprise';
import { SearchResultUser } from '@app/core/interfaces/search-result-user';
import { SearchType } from '@app/core/interfaces/search-type';
import { UserLevel } from '@app/core/models/user-level';
import { MenuService } from '@app/menu/services/menu.service';
import { RouterService } from '@app/routing/router.service';
import {
  CommandHelper,
  CommandResultEntry,
  CommandResultList,
  QuickCommandService,
} from '@app/shared/quick-commands';
import { SharedModule } from '@app/shared/shared.module';
import { isBlank } from '@app/utils/string';
import { enhanceUserWithExtension } from '@app/utils/user';
import { EntityExistsGuard } from '@business/entity-exists-guard.service';
import { SearchService } from '@business/search/search.service';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable } from 'rxjs';
import { first } from 'rxjs/operators';

@Component({
  selector: 'portal-jump-to-command',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './jump-to-command.component.html',
  styles: ``,
})
export class JumpToCommandComponent implements OnInit {
  @ViewChild('deviceTemplate', { static: true })
  deviceTemplate: TemplateRef<any>;

  @ViewChild('user', { static: true })
  userTemplate: TemplateRef<any>;

  private E164_PATTERN = /^[+]\d+/;
  private E164_REPLACE = /[^+\d]/g;
  private MAC_PATTERN = /^([0-9A-F]{12}|([0-9A-F]{2}-){5}[0-9A-F]{2})$/i;

  private mapDevice = (device: SearchResultDevice): CommandResultEntry => {
    return {
      group: device.enterpriseName,
      historyId: device.id,
      label: device.name,
      meta: {
        device,
      },
      value: device,
    };
  };

  private mapEnterprise = (enterprise: SearchResultDomain): CommandResultEntry => {
    return {
      historyId: enterprise.enterpriseName,
      label: enterprise.enterpriseName,
      meta: {
        enterprise,
      },
      value: enterprise,
    };
  };

  private mapUser = (user: SearchResultUser): CommandResultEntry => {
    return {
      group: this.authenticationService.userIsEnterpriseAdmin() ? '' : user.enterpriseName,
      historyId: user.loginName,
      label: user.loginName,
      meta: {
        user: enhanceUserWithExtension(user),
      },
      value: user,
    };
  };

  constructor(
    private authenticationService: AuthenticationService,
    private destroyRef: DestroyRef,
    private menuService: MenuService,
    private quickCommandService: QuickCommandService,
    private routerService: RouterService,
    private searchService: SearchService,
    private translateService: TranslateService,
    private entityExistsGuard: EntityExistsGuard
  ) {}

  ngOnInit(): void {
    this.addDeviceCommand();
    this.addEnterpriseCommand();
    this.addUserCommand();

    this.entityExistsGuard.userNavigationFailed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loginName) => {
        this.quickCommandService.removeHistory('jumpToUser', [], loginName);
      });
  }

  isDevice(value: any): CommandResultEntry<string, { device: SearchResultDevice }> {
    return 'label' in value && 'value' in value && 'meta' in value && 'device' in value.meta ? value : null;
  }

  isUser(value: any): CommandResultEntry<string, { user: SearchResultUser }> {
    return 'label' in value && 'value' in value && 'meta' in value && 'user' in value.meta ? value : null;
  }

  private addDeviceCommand(): void {
    const dispose = this.quickCommandService.add({
      action: (query, [device, user]: [SearchResultDevice, string]) => {
        query = query.trim();

        if (user != null) {
          return this.navigateToDevice(device, user);
        } else if (device != null) {
          if (device.lines.length === 1) {
            return this.navigateToDevice(device);
          }

          return this.pageChoicesDevice(device);
        } else if (isBlank(query) || query.trim().length < 2) {
          return CommandHelper.list({
            emptyMessage: 'quickCommands.commands.jumpTo.empty.mac',
            placeholder: `quickCommands.commands.jumpTo.placeholders.searchDevice`,
            values: [],
          });
        } else if (this.MAC_PATTERN.test(query) === false) {
          return CommandHelper.list({
            errorMessage: this.translateService.instant(`quickCommands.commands.jumpTo.invalidMac`, {
              value: query,
            }),
            placeholder: `quickCommands.commands.jumpTo.placeholders.searchDevice`,
            values: [],
          });
        }

        if (this.authenticationService.userIsEnterpriseAdmin()) {
          return this.searchEnterprise(query, SearchType.DEVICE);
        } else if (
          this.authenticationService.userIsServiceProviderAdmin() ||
          this.authenticationService.userIsSystemAdmin()
        ) {
          return this.searchReseller(query, SearchType.DEVICE);
        } else {
          return CommandHelper.list({
            values: [],
          });
        }
      },
      alwaysShowSections: true,
      authorization: {
        requiredUserLevel: UserLevel.EnterpriseAdmin,
      },
      icon: 'far fa-ski-jump',
      label: 'quickCommands.commands.jumpTo.device',
      name: 'jumpToDevice',
      template: this.userTemplate,
    });

    this.destroyRef.onDestroy(() => {
      dispose();
    });
  }

  private addEnterpriseCommand(): void {
    const dispose = this.quickCommandService.add({
      action: (query, [enterprise]: [SearchResultEnterprise, string]) => {
        query = query.trim();

        let infoMessage: string | null = null;

        if (enterprise != null) {
          return this.navigateToEnterprise(enterprise);
        } else if (isBlank(query) || query.trim().length < 2) {
          return CommandHelper.list({
            emptyMessage: 'quickCommands.commands.jumpTo.empty.blank',
            placeholder: `quickCommands.commands.jumpTo.placeholders.search`,
            rememberResult: true,
            showHistory: true,
            values: [],
          });
        }

        if (
          this.authenticationService.userIsServiceProviderAdmin() ||
          this.authenticationService.userIsSystemAdmin()
        ) {
          return this.searchReseller(query, SearchType.DOMAIN, infoMessage);
        } else {
          return CommandHelper.list({
            values: [],
          });
        }
      },
      alwaysShowSections: true,
      authorization: {
        requiredUserLevel: UserLevel.ServiceProviderAdmin,
      },
      historyCount: 5,
      hotkey: 'control.e',
      icon: 'far fa-ski-jump',
      label: 'quickCommands.commands.jumpTo.enterprise',
      name: 'jumpToEnterprise',
      template: this.userTemplate,
    });

    this.destroyRef.onDestroy(() => {
      dispose();
    });
  }

  private addUserCommand(): void {
    const dispose = this.quickCommandService.add({
      action: (query, [user]: [SearchResultUser, string]) => {
        query = query.trim();

        let infoMessage: string | null = null;

        if (user != null) {
          return this.navigateToUser(user);
        } else if (isBlank(query) || query.trim().length < 2) {
          return CommandHelper.list({
            emptyMessage: 'quickCommands.commands.jumpTo.empty.blank',
            placeholder: `quickCommands.commands.jumpTo.placeholders.search`,
            rememberResult: true,
            showHistory: true,
            values: [],
          });
        } else {
          if (this.E164_PATTERN.test(query)) {
            query = query.replace(this.E164_REPLACE, '');
            infoMessage = this.translateService.instant(
              'quickCommands.commands.jumpTo.user.cleanedQueryToE164',
              {
                value: query,
              }
            );
          }
        }

        if (this.authenticationService.userIsEnterpriseAdmin()) {
          return this.searchEnterprise(query, SearchType.END_USER, infoMessage);
        } else if (
          this.authenticationService.userIsServiceProviderAdmin() ||
          this.authenticationService.userIsSystemAdmin()
        ) {
          return this.searchReseller(query, SearchType.END_USER, infoMessage);
        } else {
          return CommandHelper.list({
            values: [],
          });
        }
      },
      alwaysShowSections: true,
      authorization: {
        requiredUserLevel: UserLevel.EnterpriseAdmin,
      },
      historyCount: 5,
      hotkey: 'control.u',
      icon: 'far fa-ski-jump',
      label: 'quickCommands.commands.jumpTo.user',
      name: 'jumpToUser',
      template: this.userTemplate,
    });

    this.destroyRef.onDestroy(() => {
      dispose();
    });
  }

  private searchEnterprise(
    query: string,
    type: SearchType,
    infoMessage?: string
  ): Observable<CommandResultList<SearchResultUser>> {
    return CommandHelper.list$(
      this.searchService.searchEnterprise(query, [type]),
      ({ searchResult }) => {
        const { devices, users } = searchResult;

        return {
          infoMessage: infoMessage ?? null,
          rememberResult: true,
          values: [...devices.map(this.mapDevice), ...users.map(this.mapUser)],
        };
      },
      {
        debounce: 400,
      }
    );
  }

  private searchReseller(
    query: string,
    type: SearchType,
    infoMessage?: string
  ): Observable<CommandResultList<any>> {
    return CommandHelper.list$(
      forkJoin([this.searchService.searchReseller(query, [type]), this.menuService.menuData$.pipe(first())]),
      ([viewModel, menuData]) => {
        const { devices, domains, users } = viewModel.searchResult;
        const values = [
          ...devices.map(this.mapDevice),
          ...domains.filter((domain) => domain.level === 'ENTERPRISE').map(this.mapEnterprise),
          ...users.map(this.mapUser),
        ];

        if (menuData.enterpriseId != null) {
          return {
            currentGroup: menuData.enterpriseId,
            infoMessage: infoMessage ?? null,
            rememberResult: true,
            values,
          };
        } else {
          return {
            infoMessage: infoMessage ?? null,
            rememberResult: true,
            values,
          };
        }
      },
      {
        debounce: 400,
      }
    );
  }

  private navigateToDevice(device: SearchResultDevice, user?: string) {
    if (user != null) {
      if (user != 'enterprise-multiline-devices') {
        const { url } = this.routerService.resolveLink('end-user-devices', {
          enterpriseId: device.enterpriseName,
          serviceProviderId: device.serviceProviderName,
          userId: device.lines[0].loginName,
        });

        this.routerService.navigateByUrl(url + `;openDevices=${device.id}`);
      } else if (device != null) {
        const { url } = this.routerService.resolveLink('enterprise-multiline-devices', {
          enterpriseId: device.enterpriseName,
          serviceProviderId: device.serviceProviderName,
        });

        this.routerService.navigateByUrl(`${url};deviceId=${device.mac}`);
      }
    } else {
      const { url } = this.routerService.resolveLink('end-user-devices', {
        enterpriseId: device.enterpriseName,
        serviceProviderId: device.serviceProviderName,
        userId: device.lines[0].loginName,
      });

      this.routerService.navigateByUrl(url + `;openDevices=${device.id}`);
    }

    return CommandHelper.close();
  }

  private navigateToEnterprise(enterprise: SearchResultEnterprise) {
    this.routerService.navigateToRoute('enterprise-settings', {
      enterpriseId: enterprise.enterpriseName,
      serviceProviderId: enterprise.serviceProviderName,
    });

    return CommandHelper.close();
  }

  private navigateToUser(user: SearchResultUser) {
    this.routerService.navigateToRoute('end-user-personalization', {
      enterpriseId: user.enterpriseName,
      serviceProviderId: user.serviceProviderName,
      userId: user.loginName,
    });

    return CommandHelper.close();
  }

  private pageChoicesDevice(device: SearchResultDevice) {
    return CommandHelper.list({
      placeholder: 'quickCommands.commands.jumpTo.placeholders.devicePage',
      values: [
        {
          label: 'quickCommands.commands.jumpTo.multilineDevices',
          value: 'enterprise-multiline-devices',
        },
        ...device.lines
          .filter((line) => line.loginName != null)
          .map((line): CommandResultEntry => {
            return {
              label: line.loginName!,
              value: line.loginName,
            };
          }),
      ],
    });
  }
}
